<template>
  <div class="p-4 md:p-6 lg:p-8 xl:p-10 bg-app-bg-light dark:bg-app-bg-dark min-h-screen">
    <!-- Header -->
    <div class="mb-6 md:mb-8">
      <h1 class="text-2xl md:text-3xl lg:text-4xl font-bold text-slate-800 dark:text-slate-100 mb-2">
        <i class="bx bx-file text-custom-primary"></i> Panel de Reportes
      </h1>
      <p class="text-sm md:text-base text-slate-600 dark:text-slate-400">
        Acceso rápido a todos los reportes del sistema - {{ fechaActual }}
      </p>
    </div>

    <!-- Administración -->
    <div class="bg-white dark:bg-slate-800 rounded-xl p-4 md:p-6 shadow-sm mb-6 md:mb-8">
      <h2 class="text-lg md:text-xl font-semibold text-slate-800 dark:text-slate-100 mb-4 md:mb-6">
        <i class="bx bx-cog text-custom-primary"></i> Administración
      </h2>
      <div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 md:gap-4">
        <button
          type="button"
          @click="navegarA('/crear-codigos-descuento')"
          class="flex flex-col items-center justify-center p-3 md:p-4 rounded-lg bg-white dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 transition-all shadow-md hover:shadow-lg group border border-slate-200 dark:border-slate-700 cursor-pointer"
        >
          <i class="bx bx-purchase-tag text-2xl md:text-3xl text-pink-600 dark:text-pink-400 mb-2 group-hover:scale-110 transition-transform"></i>
          <span class="text-xs md:text-sm text-center text-slate-700 dark:text-slate-300">Cupones</span>
        </button>
      </div>
    </div>

    <!-- Filtro de Turno -->
    <div class="bg-white dark:bg-slate-800 rounded-xl p-4 md:p-5 shadow-sm mb-6 md:mb-8">
      <h2 class="text-base font-semibold text-slate-700 dark:text-slate-200 mb-3 flex items-center gap-2">
        <i class="bx bx-filter-alt text-custom-primary"></i> Filtro de Turno
      </h2>
      <div class="flex flex-wrap gap-4 items-end">
        <div>
          <label class="text-xs text-slate-500 dark:text-slate-400 mb-1 block">Desde</label>
          <input type="datetime-local" v-model="filtro.fechaInicio"
            class="border border-slate-300 dark:border-slate-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-custom-primary" />
        </div>
        <div>
          <label class="text-xs text-slate-500 dark:text-slate-400 mb-1 block">Hasta</label>
          <input type="datetime-local" v-model="filtro.fechaFin"
            class="border border-slate-300 dark:border-slate-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-custom-primary" />
        </div>
        <button @click="cargarEstadisticas"
          class="bg-custom-primary hover:opacity-90 text-white px-4 py-2 rounded-lg text-sm font-medium transition-opacity border-0">
          <i class="bx bx-refresh mr-1"></i>Actualizar
        </button>
      </div>
    </div>

    <!-- Stats Grid -->
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6 md:mb-8">
      <!-- Ingresos del Día -->
      <div class="bg-white dark:bg-slate-800 rounded-xl p-4 md:p-6 shadow-sm hover:shadow-md transition-shadow">
        <div class="flex items-center justify-between mb-3">
          <div class="p-2 md:p-3 bg-green-100 dark:bg-green-900/30 rounded-lg">
            <i class="bx bx-dollar text-2xl md:text-3xl text-green-600 dark:text-green-400"></i>
          </div>
          <span class="text-xs md:text-sm font-medium text-green-600 dark:text-green-400">
            <i class="bx bx-trending-up"></i>
          </span>
        </div>
        <h3 class="text-xl md:text-2xl font-bold text-slate-800 dark:text-slate-100 mb-1">
          Q{{ formatNumber(stats.ingresosHoy) }}
        </h3>
        <p class="text-xs md:text-sm text-slate-600 dark:text-slate-400">Ingresos de Hoy</p>
      </div>

      <!-- Ingresos del Mes -->
      <div class="bg-white dark:bg-slate-800 rounded-xl p-4 md:p-6 shadow-sm hover:shadow-md transition-shadow">
        <div class="flex items-center justify-between mb-3">
          <div class="p-2 md:p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
            <i class="bx bx-chart text-2xl md:text-3xl text-blue-600 dark:text-blue-400"></i>
          </div>
          <span class="text-xs md:text-sm font-medium text-blue-600 dark:text-blue-400">
            Mes actual
          </span>
        </div>
        <h3 class="text-xl md:text-2xl font-bold text-slate-800 dark:text-slate-100 mb-1">
          Q{{ formatNumber(stats.ingresosMes) }}
        </h3>
        <p class="text-xs md:text-sm text-slate-600 dark:text-slate-400">Ingresos del Mes</p>
      </div>

      <!-- Ocupación -->
      <div class="bg-white dark:bg-slate-800 rounded-xl p-4 md:p-6 shadow-sm hover:shadow-md transition-shadow">
        <div class="flex items-center justify-between mb-3">
          <div class="p-2 md:p-3 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
            <i class="bx bx-bed text-2xl md:text-3xl text-purple-600 dark:text-purple-400"></i>
          </div>
          <span class="text-xs md:text-sm font-medium text-slate-500 dark:text-slate-400">
            {{ ocupacionPorcentaje }}%
          </span>
        </div>
        <h3 class="text-xl md:text-2xl font-bold text-slate-800 dark:text-slate-100 mb-1">
          {{ stats.habitacionesOcupadas }}/{{ stats.totalHabitaciones }}
        </h3>
        <p class="text-xs md:text-sm text-slate-600 dark:text-slate-400">Habitaciones Ocupadas</p>
      </div>

      <!-- Reservas Pendientes de Pago -->
      <div class="bg-white dark:bg-slate-800 rounded-xl p-4 md:p-6 shadow-sm hover:shadow-md transition-shadow">
        <div class="flex items-center justify-between mb-3">
          <div class="p-2 md:p-3 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg">
            <i class="bx bx-money text-2xl md:text-3xl text-yellow-600 dark:text-yellow-400"></i>
          </div>
        </div>
        <h3 class="text-xl md:text-2xl font-bold text-slate-800 dark:text-slate-100 mb-1">
          {{ stats.reservasPendientesPago }}
        </h3>
        <p class="text-xs md:text-sm text-slate-600 dark:text-slate-400">Reservas con Saldo</p>
      </div>
    </div>

    <!-- Reportes de Hotel -->
    <div class="bg-white dark:bg-slate-800 rounded-xl p-4 md:p-6 shadow-sm mb-6 md:mb-8">
      <h2 class="text-lg md:text-xl font-semibold text-slate-800 dark:text-slate-100 mb-4 md:mb-6">
        <i class="bx bx-hotel text-custom-primary"></i> Reportes de Hotel
      </h2>
      <div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 md:gap-4">
        <button
          type="button"
          @click="navegarA('/reporte-pagos-dia')"
          class="flex flex-col items-center justify-center p-3 md:p-4 rounded-lg bg-white dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 transition-all shadow-md hover:shadow-lg group border border-slate-200 dark:border-slate-700 cursor-pointer"
        >
          <i class="bx bx-dollar-circle text-2xl md:text-3xl text-green-600 dark:text-green-400 mb-2 group-hover:scale-110 transition-transform"></i>
          <span class="text-xs md:text-sm text-center text-slate-700 dark:text-slate-300">Pagos por Día</span>
        </button>

        <button
          type="button"
          @click="navegarA('/reportes-hotel')"
          class="flex flex-col items-center justify-center p-3 md:p-4 rounded-lg bg-white dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 transition-all shadow-md hover:shadow-lg group border border-slate-200 dark:border-slate-700 cursor-pointer"
        >
          <i class="bx bx-bed text-2xl md:text-3xl text-blue-600 dark:text-blue-400 mb-2 group-hover:scale-110 transition-transform"></i>
          <span class="text-xs md:text-sm text-center text-slate-700 dark:text-slate-300">Hab. / Reservas</span>
        </button>

        <button
          type="button"
          @click="navegarA('/ingresos-hospedaje')"
          class="flex flex-col items-center justify-center p-3 md:p-4 rounded-lg bg-white dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 transition-all shadow-md hover:shadow-lg group border border-slate-200 dark:border-slate-700 cursor-pointer"
        >
          <i class="bx bx-line-chart text-2xl md:text-3xl text-purple-600 dark:text-purple-400 mb-2 group-hover:scale-110 transition-transform"></i>
          <span class="text-xs md:text-sm text-center text-slate-700 dark:text-slate-300">Ingresos Hotel</span>
        </button>

        <button
          type="button"
          @click="navegarA('/reporte-camas')"
          class="flex flex-col items-center justify-center p-3 md:p-4 rounded-lg bg-white dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 transition-all shadow-md hover:shadow-lg group border border-slate-200 dark:border-slate-700 cursor-pointer"
        >
          <i class="bx bx-home-heart text-2xl md:text-3xl text-orange-600 dark:text-orange-400 mb-2 group-hover:scale-110 transition-transform"></i>
          <span class="text-xs md:text-sm text-center text-slate-700 dark:text-slate-300">Camas por Habitación</span>
        </button>

        <button
          type="button"
          @click="navegarA('/reservas-pendientes-de-pago')"
          class="flex flex-col items-center justify-center p-3 md:p-4 rounded-lg bg-white dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 transition-all shadow-md hover:shadow-lg group border border-slate-200 dark:border-slate-700 cursor-pointer"
        >
          <i class="bx bx-money-withdraw text-2xl md:text-3xl text-yellow-600 dark:text-yellow-400 mb-2 group-hover:scale-110 transition-transform"></i>
          <span class="text-xs md:text-sm text-center text-slate-700 dark:text-slate-300">Reservas Pendientes</span>
        </button>
      </div>
    </div>

    <!-- Reportes de Restaurante -->
    <div class="bg-white dark:bg-slate-800 rounded-xl p-4 md:p-6 shadow-sm mb-6 md:mb-8">
      <h2 class="text-lg md:text-xl font-semibold text-slate-800 dark:text-slate-100 mb-4 md:mb-6">
        <i class="bx bx-restaurant text-custom-primary"></i> Reportes de Restaurante
      </h2>
      <div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 md:gap-4">
        <button
          type="button"
          @click="navegarA('/reportes-restaurante')"
          class="flex flex-col items-center justify-center p-3 md:p-4 rounded-lg bg-white dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 transition-all shadow-md hover:shadow-lg group border border-slate-200 dark:border-slate-700 cursor-pointer"
        >
          <i class="bx bx-food-menu text-2xl md:text-3xl text-red-600 dark:text-red-400 mb-2 group-hover:scale-110 transition-transform"></i>
          <span class="text-xs md:text-sm text-center text-slate-700 dark:text-slate-300">Ventas por Fecha</span>
        </button>
      </div>
    </div>

    <!-- Reportes de Caja -->
    <div class="bg-white dark:bg-slate-800 rounded-xl p-4 md:p-6 shadow-sm">
      <h2 class="text-lg md:text-xl font-semibold text-slate-800 dark:text-slate-100 mb-4 md:mb-6">
        <i class="bx bx-wallet text-custom-primary"></i> Reportes de Caja
      </h2>
      <div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 md:gap-4">
        <button
          type="button"
          @click="navegarA('/cierre-caja-diario')"
          class="flex flex-col items-center justify-center p-3 md:p-4 rounded-lg bg-white dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 transition-all shadow-md hover:shadow-lg group border border-slate-200 dark:border-slate-700 cursor-pointer"
        >
          <i class="bx bx-calculator text-2xl md:text-3xl text-indigo-600 dark:text-indigo-400 mb-2 group-hover:scale-110 transition-transform"></i>
          <span class="text-xs md:text-sm text-center text-slate-700 dark:text-slate-300">Cierre de Caja</span>
        </button>

        <button
          type="button"
          @click="navegarA('/cierre-caja-fechas')"
          class="flex flex-col items-center justify-center p-3 md:p-4 rounded-lg bg-white dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 transition-all shadow-md hover:shadow-lg group border border-slate-200 dark:border-slate-700 cursor-pointer"
        >
          <i class="bx bx-calendar-check text-2xl md:text-3xl text-teal-600 dark:text-teal-400 mb-2 group-hover:scale-110 transition-transform"></i>
          <span class="text-xs md:text-sm text-center text-slate-700 dark:text-slate-300">Cierre por Fechas</span>
        </button>

        <button
          type="button"
          @click="navegarA('/depositos-efectivo')"
          class="flex flex-col items-center justify-center p-3 md:p-4 rounded-lg bg-white dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 transition-all shadow-md hover:shadow-lg group border border-slate-200 dark:border-slate-700 cursor-pointer"
        >
          <i class="bx bx-coin-stack text-2xl md:text-3xl text-emerald-600 dark:text-emerald-400 mb-2 group-hover:scale-110 transition-transform"></i>
          <span class="text-xs md:text-sm text-center text-slate-700 dark:text-slate-300">Resumen Efectivo</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { API_URL } from '../../config/config.js';

export default {
  name: 'DashboardReportes',
  data() {
    // Turno hotelero: 8am hoy → 8am mañana (unificado con cierre-caja-diario)
    const hoy = new Date();
    const inicio = new Date(hoy); inicio.setHours(8, 0, 0, 0);
    const fin = new Date(hoy); fin.setDate(fin.getDate() + 1); fin.setHours(8, 0, 0, 0);
    const toLocal = d => new Date(d - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
    return {
      filtro: {
        fechaInicio: toLocal(inicio),
        fechaFin: toLocal(fin)
      },
      stats: {
        ingresosHoy: 0,
        ingresosMes: 0,
        habitacionesOcupadas: 0,
        totalHabitaciones: 0,
        reservasPendientesPago: 0
      },
      fechaActual: ''
    };
  },
  computed: {
    ocupacionPorcentaje() {
      if (this.stats.totalHabitaciones === 0) return 0;
      return Math.round((this.stats.habitacionesOcupadas / this.stats.totalHabitaciones) * 100);
    }
  },
  mounted() {
    this.obtenerFechaActual();
    this.cargarEstadisticas();
  },
  methods: {
    obtenerFechaActual() {
      const opciones = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
      this.fechaActual = new Date().toLocaleDateString('es-ES', opciones);
    },

    async cargarEstadisticas() {
      try {
        const token = localStorage.getItem('access_token');
        const response = await axios.get(`${API_URL}/dashboard/reportes`, {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            fecha_inicio: this.filtro.fechaInicio,
            fecha_fin: this.filtro.fechaFin
          }
        });

        this.stats = response.data;
      } catch (error) {
        console.error('Error al cargar estadísticas:', error);
        if (this.$toast) {
          this.$toast.error('Error al cargar las estadísticas del dashboard');
        }
      }
    },

    formatNumber(value) {
      if (!value) return '0.00';
      return parseFloat(value).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    },

    navegarA(ruta) {
      this.$router.push(ruta);
    }
  }
};
</script>

<style scoped>
/* Responsive adjustments */
@media (max-width: 640px) {
  .grid {
    gap: 0.75rem;
  }
}

/* Asegurar que los botones sean clickeables */
button {
  user-select: none;
  -webkit-user-select: none;
  outline: none;
}

button i,
button span {
  pointer-events: none;
}
</style>
